import { Component, ElementRef, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MbqService } from '../mbq.service';
import { MbqComponent } from '../mbq.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-mbq-draft-list',
  imports: [FormsModule, RouterModule, CommonModule, MbqComponent],
  templateUrl: './mbq-draft-list.component.html',
  styleUrl: './mbq-draft-list.component.scss',
})
export class MbqDraftListComponent {
  draftList: any[] = [];
  loading = false;
  activeTab: string = 'mbqDraftList';
  searchTerm = '';
  filterMode = ''; // FULL | PARTIAL | ''
  pageSize = 10;
  currentPage = 1;
  pageCount = 1;
  uploadUrl = `${environment.apiUrl}/mbq-draft/upload`;
  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(
    private mbqService: MbqService,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.fetchMbqDrafts();
  }

  fetchMbqDrafts(): void {
    this.loading = true;

    const params: any = {
      q: this.searchTerm || undefined,
      mode: this.filterMode || undefined,
      limit: this.pageSize,
      page: this.currentPage,
    };

    this.mbqService.getMbqDrafts(params).subscribe({
      next: (response: any) => {
        this.draftList = response?.data;

        const total = response?.total || 0;
        this.pageCount = Math.ceil(total / this.pageSize) || 1;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching stock takes', err);
        this.toastr.error('Failed to load stock takes', 'Error');
        this.loading = false;
      },
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.fetchMbqDrafts();
  }

  pageSizeChange(): void {
    this.currentPage = 1;
    this.fetchMbqDrafts();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.fetchMbqDrafts();
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  downloadMbqDraft(id: string): void {
    this.mbqService.downloadMbqDraftPDF(id).subscribe({
      next: (res: any) => {
        const blob = new Blob([res], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `StockTake-${id}.pdf`;
        a.click();
      },
      error: () => this.toastr.error('Failed to download PDF'),
    });
  }

  deleteStockTake(id: string): void {
    if (!confirm('Are you sure you want to delete this draft?')) return;

    this.mbqService.deleteMbqDraft(id).subscribe({
      next: () => {
        this.toastr.success('MBQ draft deleted successfully');
        this.fetchMbqDrafts();
      },
      error: () => {
        this.toastr.error('Failed to delete draft');
      },
    });
  }

  setTab(tab: 'mbqDraftList' | 'mbqList') {
    this.activeTab = tab;
    localStorage.setItem('activeVendorTab', tab);
  }

  // --- Bulk Operation Logic ---

  downloadTemplate(): void {
    this.mbqService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(blob, 'mbq-template.xlsx');
      },
      error: (error: any) => {
        console.error('Error downloading template:', error);
        this.toastr.error('Failed to download template.');
      },
    });
  }

  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
    this.toastr.success('File downloaded successfully!');
  }

  triggerUpload(): void {
    this.fileInput.nativeElement.value = ''; // Reset input
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      this.toastr.error('Invalid file format. Please upload an Excel file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    this.mbqService.http.post(this.uploadUrl, formData).subscribe({
      next: (res: any) => this.onUploadSuccess('Bulk upload successful!'),
      error: (err: any) => this.onUploadError(err),
    });
  }

  onUploadSuccess(message: string): void {
    this.toastr.success(message);
    this.fetchMbqDrafts();
  }

  onUploadError(error: any): void {
    console.error('Bulk upload error:', error);
    const errorMessage =
      error.error?.message || error.message || 'Failed to upload.';
    this.toastr.error(errorMessage);
  }
}
